/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Checkbox, Grid, IconButton, makeStyles } from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import Button from 'components/Button';
import { DialogAlertProps } from 'components/DialogAlert';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  getAllRolesRequest,
  registerRoleRequest,
  removeRolesRequest,
  resetGroupsSearch,
} from 'modules/permission/actions';
import { mapPermissionName } from 'modules/permission/helpers';
import {
  ListRoleDataProps,
  RoleFields,
  RoleProps,
} from 'modules/permission/interfaces';
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import ApplicationState from 'store/interfaces';
import { formatDateWithMoment } from 'utils/helpers';
import Permission from 'components/ValidPermission';
import { useTableFilters } from 'hooks/useTableFilters';
import FormGroup from '../FormGroup';
import styles from '../Template/styles';
import TemplateGroupsList from './TemplateGroupsList';
import FormCloneGroup from '../FormCloneGroup';

const useStyles = makeStyles(styles);

interface Props {
  isLoading: boolean;
  roleList: ListRoleDataProps;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  getAllRolesRequest: (props) => void;
  registerRoleRequest: (data: RoleFields) => Promise<void>;
  removeRolesRequest: (ids: string[]) => Promise<void>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  resetGroupsSearch: () => void;
}

const ListGroups = ({
  isLoading,
  roleList,
  role,
  permissions,
  getAllRolesRequest,
  registerRoleRequest,
  removeRolesRequest,
  modalOpen,
  modalClose,
  resetGroupsSearch,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setQuery,
  } = useTableFilters();

  const [searchValue, setSearchValue] = React.useState('');
  const [groups, setGroups] = React.useState<any>();
  const [checked, setChecked] = React.useState<string[]>([]);
  const [alert] = React.useState<DialogAlertProps | null>(null);
  const [refresh, setRefresh] = React.useState(false);
  const [listSwitch, setListSwitch] = React.useState(false);
  const dispatch = useDispatch();

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) =>
      setChecked(prev =>
        prev.includes(value)
          ? prev.filter(item => item !== value)
          : [...prev, value]
      ),
    toggleAll: (event: React.ChangeEvent<HTMLInputElement>) =>
      setChecked(
        event.target.checked
          ? groups.results.map(({ role_uuid }) => role_uuid)
          : []
      ),
  };

  const getRoles = (refresh = false) => {
    getAllRolesRequest({
      operator_role: role,
      query,
    });

    setRefresh(refresh);
  };

  const handleGetRoles = () => {
    getAllRolesRequest({
      operator_role: role,
      query,
    });
    setListSwitch(true);
  };

  React.useEffect(() => {
    if (listSwitch === true) {
      getRoles();
    }
  }, [query]);

  React.useEffect(() => {
    return () => {
      dispatch(resetGroupsSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const addGroup = () => {
    modalOpen({
      title: i18n.t('ADD_GROUP_KEY'),
      content: (
        <FormGroup
          onSubmit={values => registerRoleRequest(values)}
          onCancel={modalClose}
          isLoading={isLoading}
        />
      ),
      maxWidth: 'sm',
      noBorder: true,
    });
  };

  const removeGroups = (ids: string[]) => {
    const item = groups?.results.find(({ role_uuid }) => role_uuid === ids[0]);
    const { name } = item;

    modalOpen({
      title: i18n.t(
        ids.length === 1
          ? 'GROUPS_PAGE.WANT_REMOVE_GROUP_KEY'
          : 'GROUPS_PAGE.WANT_REMOVE_SELECTED_GROUPS_KEY'
      ),
      content: i18n.t(
        ids.length === 1
          ? 'GROUPS_PAGE.REMOVE_WARNING_MSG_KEY'
          : 'GROUPS_PAGE.ACTION_IS_IRREVERSIBLE_KEY',
        {
          name,
        }
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeRolesRequest(ids);
              modalClose();
              getAllRolesRequest({
                operator_role: role,
                query,
              });
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const openFormCloneGroup = (item: RoleProps) =>
    modalOpen({
      title: i18n.t('GROUPS_PAGE.CLONE_GROUP_KEY'),
      content: <FormCloneGroup groupToClone={item} query={query} />,
      maxWidth: 'sm',
      noBorder: true,
    });

  React.useEffect(() => {
    setGroups(roleList);
  }, [roleList]);

  return (
    <React.Fragment>
      <TemplateGroupsList
        title={i18n.t('GROUPS_PAGE.LIST_GROUPS_TITLE_KEY')}
        subtitle={i18n.t('GROUPS_PAGE.LIST_GROUPS_SUBTITLE_KEY')}
        contentTitle={i18n.t('GROUPS_PAGE.LIST_GROUPS_CONTENT_TITLE_KEY')}
        contentSubtitle={i18n.t('GROUPS_PAGE.LIST_GROUPS_CONTENT_SUBTITLE_KEY')}
        alert={alert}
        generalActions={[
          {
            label: i18n.t('ADD_GROUP_KEY'),
            onClick: addGroup,
            disabled: !!permissions && !permissions.create_all_roles,
          },
          {
            label: i18n.t('REMOVE_SELECTED_GROUPS_KEY'),
            disabled: checked.length === 0,
            onClick: () => removeGroups(checked),
          },
        ]}
        onRefresh={() => getRoles(true)}
        onSearch={handleSearch}
        marginSearchTop
        listSwitch={listSwitch}
        searchField={searchValue}
        setListSwitch={setListSwitch}
        setSearchField={setSearchValue}
        initialQuery={query}
        setQuery={setQuery}
        getRoles={getRoles}
        handleGetRoles={handleGetRoles}
        isLoading={isLoading}
      >
        <TableItems
          header={
            <>
              <TableCell className={classes.checkboxCell}>
                <Checkbox onChange={handleCheck.toggleAll} />
              </TableCell>
              <TableCell centerTitle>{i18n.t('ACCESS_KEY')}</TableCell>
              <TableCell order handleOrder={() => handleOrder('name')}>
                {i18n.t('GROUP_KEY')}
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('created_by')}>
                {i18n.t('CREATED_BY_KEY')}
              </TableCell>
              <TableCell
                order
                centerTitle
                handleOrder={() => handleOrder('created_at')}
              >
                {i18n.t('CREATED_AT_KEY')}
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('updated_by')}>
                {i18n.t('UPDATED_BY_KEY')}
              </TableCell>
              <TableCell order handleOrder={() => handleOrder('from_template')}>
                {i18n.t('TEMPLATE_KEY')}
              </TableCell>
              <TableCell
                order
                centerTitle
                handleOrder={() => handleOrder('active')}
              >
                {i18n.t('STATUS_KEY')}
              </TableCell>
              <TableCell centerTitle>{i18n.t('ACTIONS_KEY')}</TableCell>
            </>
          }
          renderItems={(item: RoleProps, index) => (
            <TableRow key={item?.role_uuid}>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  onChange={() => handleCheck.toggle(item?.role_uuid)}
                  checked={handleCheck.exists(item?.role_uuid)}
                />
              </TableCell>
              <TableCell centerTitle textAlignCenter>
                <IconButton
                  id={`access-group-${index}`}
                  className={classes.accessButton}
                  onClick={() => history.push(`/groups/${item?.role_uuid}`)}
                >
                  <InputIcon />
                </IconButton>
              </TableCell>
              <TableCell className={classes.textField} tooltip={item?.name}>
                {item?.name}
              </TableCell>
              <TableCell tooltip={item?.created_by}>
                {item?.created_by}
              </TableCell>
              <TableCell className={classes.dateField}>
                <span>{formatDateWithMoment(item?.created_at)}</span>
              </TableCell>
              <TableCell tooltip={item?.updated_by}>
                {item?.updated_by}
              </TableCell>
              <TableCell tooltip={item?.from_template}>
                {mapPermissionName(item?.from_template)}
              </TableCell>
              <TableCell
                align="center"
                order
                handleOrder={() => handleOrder('active')}
              >
                <StatusIcon status={item?.active ? 'success' : 'inactive'} />
              </TableCell>
              <TableCell centerTitle align="center" textAlignCenter>
                <MenuActions id={`btn-options-group-${index}`}>
                  <MenuActionItem
                    id="btn-edit"
                    onClick={() => history.push(`/groups/${item?.role_uuid}`)}
                  >
                    {i18n.t('EDIT_KEY')}
                  </MenuActionItem>
                  <MenuActionItem
                    id="btn-remover"
                    onClick={() => removeGroups([item?.role_uuid])}
                  >
                    {i18n.t('REMOVE_KEY')}
                  </MenuActionItem>
                  <Permission value="can_clone_role">
                    <MenuActionItem
                      id="btn-clone"
                      onClick={() => openFormCloneGroup(item)}
                    >
                      {i18n.t('CLONE_KEY')}
                    </MenuActionItem>
                  </Permission>
                </MenuActions>
              </TableCell>
            </TableRow>
          )}
          data={groups}
          isLoading={!refresh && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('GROUPS_PAGE.NO_GROUPS_KEY')}
        />
      </TemplateGroupsList>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  permission: { isLoading, roleList },
}: ApplicationState) => ({
  isLoading,
  roleList,
  permissions: data?.permissions,
  role: data?.role,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  getAllRolesRequest,
  registerRoleRequest,
  removeRolesRequest,
  resetGroupsSearch,
})(ListGroups);
